import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Alert, Card, CardContent } from '@mui/material';
import { Loader2 } from 'lucide-react';
import { API_URL } from '../../../utils/apiConfig';

export default function PersonalInformationForm({
  fields,
  responses,
  employee,
  formId,
  employeeId,
  onSubmit,
  isCompleted,
}) {
  const personalInfoFields = [
    { key: 'firstName', label: 'First Name', type: 'text', isRequired: true },
    { key: 'lastName', label: 'Last Name', type: 'text', isRequired: true },
    { key: 'dob', label: 'Date of Birth', type: 'date', isRequired: true },
    { key: 'ssn', label: 'Social Security Number', type: 'text', isRequired: true },
    { key: 'phone', label: 'Phone Number', type: 'tel', isRequired: true },
    { key: 'address', label: 'Address', type: 'text', isRequired: true },
  ];

  const initializeFormData = () => {
    const initialData = {};
    personalInfoFields.forEach((field) => {
      const response = responses?.find((r) => r.fieldKey === field.key);
      if (response) {
        initialData[field.key] = response.fieldValue;
      } else if (employee && employee[field.key]) {
        initialData[field.key] =
          field.type === 'date'
            ? new Date(employee[field.key]).toISOString().split('T')[0]
            : employee[field.key];
      } else {
        initialData[field.key] = '';
      }
    });
    return initialData;
  };

  const [formData, setFormData] = useState(initializeFormData());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setFormData(initializeFormData());
  }, [employee, responses]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const requiredFields = personalInfoFields.filter((field) => field.isRequired);
    for (const field of requiredFields) {
      if (!formData[field.key] || formData[field.key].trim() === '') {
        setError(`${field.label} is required`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const payload = {
        formId: formId.toString(),
        formData,
      };

      const response = await fetch(`${API_URL}/forms/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new Error(responseData.message || 'Failed to save form data');
      }

      setSuccess(true);
      onSubmit && (await onSubmit({ formId, formData, status: 'completed' }));
    } catch (err) {
      setError(err.message || 'Failed to save form data.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      maxWidth: 800, 
      margin: '0 auto',
      py: 4,
    }}>
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '0.95rem'
            }
          }}
        >
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '0.95rem'
            }
          }}
        >
          Personal information saved successfully!
        </Alert>
      )}

      {isCompleted && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            '& .MuiAlert-message': {
              fontSize: '0.95rem'
            }
          }}
        >
          This form has been completed. You can still make updates if needed.
        </Alert>
      )}

      <Card 
        elevation={0}
        sx={{ 
          mb: 3, 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            p: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
          }}>
            <Typography 
              variant="h6"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'text.primary'
              }}
            >
              Personal Information
              <Typography 
                component="span" 
                sx={{ 
                  ml: 1,
                  color: 'error.main',
                  fontSize: '0.875rem',
                  fontWeight: 400
                }}
              >
                (Required)
              </Typography>
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            {personalInfoFields.map((field) => (
              <TextField
                key={field.key}
                label={field.label}
                name={field.key}
                value={formData[field.key] || ''}
                onChange={handleChange}
                fullWidth
                required={field.isRequired}
                type={field.type}
                variant="outlined"
                InputLabelProps={field.type === 'date' ? { shrink: true } : {}}
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'grey.50',
                    '&:hover': {
                      bgcolor: 'background.paper',
                    },
                    '&.Mui-focused': {
                      bgcolor: 'background.paper',
                    }
                  }
                }}
                error={field.isRequired && !formData[field.key]}
                helperText={field.isRequired && !formData[field.key] ? `${field.label} is required` : ''}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Button
        onClick={handleSave}
        variant="contained"
        disabled={loading}
        fullWidth
        sx={{ 
          mt: 2,
          py: 1.5,
          bgcolor: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.dark',
          }
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Loader2 size={18} className="animate-spin" />
            <Box component="span" sx={{ ml: 1 }}>Saving...</Box>
          </Box>
        ) : (
          'Save Personal Information'
        )}
      </Button>

      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          mt: 2.5,
          textAlign: 'center',
          color: 'text.secondary'
        }}
      >
        * All fields are required
      </Typography>
    </Box>
  );
}